import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";

const HERO_IMG = "/images/hero.webp";
const EASE = [0.16, 1, 0.3, 1];

export const Hero = ({ t }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.18]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-start sm:items-center overflow-hidden"
    >
      <div className="absolute inset-0 overflow-hidden">
        <motion.img
          src={HERO_IMG}
          alt="Bespoke Italian interior"
          style={{ y: imgY, scale: imgScale }}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#150B06]/55" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#150B06]/80 via-[#150B06]/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#150B06]/70 to-transparent" />
      </div>

      <motion.div
        style={{ y: textY, opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full pt-32 sm:pt-36 lg:pt-24"
      >
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.15 }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="h-px w-12 bg-[#D9B36A]" />
            <p
              data-testid="hero-overline"
              className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.18em] sm:tracking-[0.25em] text-[#D9B36A]"
            >
              {t.hero.overline}
            </p>
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-sans font-extralight text-[2rem] sm:text-6xl lg:text-7xl text-[#F5F2EC] tracking-tight leading-[1.12]"
          >
            {t.hero.titleLines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.3 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            data-testid="hero-subtitle"
            className="mt-8 text-base sm:text-lg font-light text-[#F5F2EC]/90 max-w-xl leading-relaxed"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <a
              href="#quote"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#2D1810] px-9 py-4 text-xs sm:text-sm font-semibold uppercase tracking-[0.15em] rounded-full hover:bg-[#c9a227] hover:-translate-y-0.5 transition-[transform,background-color] duration-300"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={15} />
            </a>
            <a
              href="#craft"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center border border-[#F5F2EC]/50 text-[#F5F2EC] px-9 py-4 text-xs sm:text-sm font-semibold uppercase tracking-[0.15em] rounded-full backdrop-blur-sm hover:bg-[#F5F2EC]/10 hover:border-[#F5F2EC] transition-[background-color,border-color] duration-300"
            >
              {t.hero.ctaSecondary}
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-3"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#F5F2EC]/60">Est. 1958</span>
        <span className="w-px h-10 bg-gradient-to-b from-[#D9B36A] to-transparent" />
      </motion.div>
    </section>
  );
};
